import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
  makeStyles,
  useMediaQuery,
} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {InboxOutlined} from '@mui/icons-material';
import {Alert} from '@mui/material';
import {isLoaded} from 'Core/models/Async';
import {getQuoteDetails, setSelectedProduct} from 'Quoting/ducks/createQuote';
import {clearCopyQuoteAlertMessage} from 'Quoting/ducks/opportunity';
import {QuoteActionSource, updateQuotesSelectedForAction} from 'Quoting/ducks/quoteActions';
import {OpportunityQuote} from 'Quoting/models/DTOs/Opportunity';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {QuoteStatus} from 'Quoting/models/enum';
import {getFormattedDate} from 'Quoting/utils/dateUtils';
import {formatPremium, safeRenderValue} from 'Quoting/utils/utilities';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import ScrollToTop from 'Shared/components/ScrollToTop';
import {capitalizeSubString} from 'Shared/utilities/stringFormatHelpers';
import {themeColors} from 'Theme';
import classNames from 'classnames';
import React, {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {QuoteFormControlElement} from '../shared/QuoteFormControlElement';
import {QuoteActionMenu} from './QuoteActions/QuoteActionMenu';
import {getApplicableActions} from './QuoteActions/QuoteActionUtils';
import {SelectedQuoteDetails} from './QuotesTab';
import {getProductByModel} from './ProductSelection';

const useQuoteCardStyles = makeStyles(theme => ({
  quoteAction: {
    maxWidth: 'max-content',
    marginLeft: '0.5rem',
    '& : nth-of-type(1)': {
      marginLeft: '1rem',
    },
  },

  primary: {
    color: themeColors.blue_400,
  },

  quoteCard: {
    alignItems: 'center',
    boxShadow: '0px 3px 3px 0px #00000026',
    border: '1px solid #D8D8D8',
    borderRadius: '0.375rem',
    backgroundColor: themeColors.primaryWhite,
    justifyContent: 'space-around',
    padding: '0 1.5rem',
    [theme.breakpoints.down('md')]: {
      maxWidth: '23rem',
      height: '100%',
      padding: '0',
    },
  },

  plan: {
    fontSize: '1.125rem',
  },

  fieldValue: {
    fontWeight: 600,
  },

  quotePremiumBg: {
    backgroundColor: '#F3F7FD',
  },

  quotePremium: {
    alignItems: 'center',
    alignSelf: 'stretch',
    justifyContent: 'center',
    '&>div > :nth-child(1)': {
      fontSize: '1.5rem',
    },
    '&>div > :nth-child(2)': {
      fontSize: '0.875rem',
      color: '#333333',
    },
    [theme.breakpoints.down('md')]: {
      justifyContent: 'center',
      flexDirection: 'column',
      marginTop: '1rem',
    },
  },

  quoteField: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem 0.20rem',
    '& :last-child': {
      fontWeight: 600,
    },
  },

  quoteSelectionCheckbox: {
    maxWidth: 'max-content',
  },

  divider: {
    width: '100%',
    marginBottom: '1rem',
  },

  quoteTitleLink: {
    cursor: 'pointer',
  },
  noRecord: {
    display: 'grid',
    placeItems: 'center',
    height: '15vh',
    width: '100%',
    textAlign: 'center',
    padding: '15px 5px',
  },
  alignRecordCenter: {
    width: '50vw',
    height: '100px',
    paddingTop: 'inherit',
  },
  success: {
    '& .MuiAlert-icon': {
      color: `${themeColors.green_500} !important`,
    },
  },
  error: {
    '& .MuiAlert-icon': {
      color: `${themeColors.red_600} !important`,
    },
  },
}));

const productModelToCdMapping = {
  ACCMaster: 'ACC',
  CIMaster: 'CI',
  HIMaster: 'HI',
  GPLUMaster: 'PL',
};

const useQuoteCardFieldElements = (quote: OpportunityQuote, classes: ReturnType<typeof useQuoteCardStyles>) => {
  const {availableQuoteActionsMap, selectedOpportunityId} = useSelector((state: ReducerState) => state.opportunity);
  const {selectedQuotesForAction} = useSelector((state: ReducerState) => state.quoteActions);

  const handleQuoteSelectionChange = useCallback(
    (quoteDetails: SelectedQuoteDetails) => dispatch(updateQuotesSelectedForAction(quoteDetails)),
    [selectedQuotesForAction]
  );

  const isSelected = Boolean(quote?.entity?._key?.rootId && selectedQuotesForAction[quote?.entity?._key?.rootId]);

  const isMobileView = !useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const {entity, premium} = quote;

  const dispatch = useDispatch();

  return useMemo(
    () => [
      {
        key: 'quote-select',
        element: (
          <QuoteFormControlElement
            controlType="checkbox"
            aria-label={`Select quote ${entity?.policyNumber}`}
            checked={isSelected}
            onChange={() =>
              handleQuoteSelectionChange({
                productModel: entity?.productCd,
                rootId: entity?._key?.rootId,
                revisionNo: entity?._key?.revisionNo,
                status: entity?.state,
                policyNumber: entity?.policyNumber,
                effectiveDate: entity?.effectiveDate,
              })
            }
          />
        ),
      },
      {
        key: 'quote-plan-details',
        element: (
          <>
            <Typography className={classNames(classes.primary, classes.fieldValue, classes.plan)}>
              <Link
                className={classes.quoteTitleLink}
                onClick={() => {
                  dispatch(setSelectedProduct(getProductByModel(entity?.productCd as A360ProductModel)));
                  dispatch(
                    getQuoteDetails(
                      entity?.productCd as A360ProductModel,
                      entity?._key?.rootId,
                      entity?._key?.revisionNo,
                      selectedOpportunityId
                    )
                  );
                }}
              >
                <span>
                  {productModelToCdMapping[safeRenderValue(entity?.productCd)]}
                  {entity?.benefitPackagingDetail?.plans?.[0]?.planCd &&
                    safeRenderValue(` (${entity?.benefitPackagingDetail?.plans?.[0]?.planCd}) `, false, '')}
                </span>
              </Link>
            </Typography>
            <>
              {!isMobileView && (
                <Typography className={classes.fieldValue}>{safeRenderValue(entity?.policyNumber)}</Typography>
              )}
            </>
          </>
        ),
      },
      {
        key: 'quote-policy-number',
        element: (
          <Typography className={classNames(classes.fieldValue)}>{safeRenderValue(entity?.policyNumber)}</Typography>
        ),
      },
      {
        key: 'quote-status',
        element: (
          <>
            <Typography className={classNames(classes.primary, classes.fieldValue, classes.plan)}>
              {capitalizeSubString(safeRenderValue(entity?.state), 0, 1)}
            </Typography>
            {entity?.effectiveDate && <Typography>Effective on {getFormattedDate(entity?.effectiveDate)}</Typography>}
          </>
        ),
      },
      {
        key: 'quote-num-of-plans',
        element: (
          <>
            <Typography>Plans</Typography>
            <Typography>{safeRenderValue(entity?.numberOfPlans)}</Typography>
          </>
        ),
      },
      {
        key: 'quote-num-of-lives',
        element: (
          <>
            <Typography># of Lives</Typography>
            <Typography>{safeRenderValue(premium?.numberOfLives)}</Typography>
          </>
        ),
      },
      {
        key: 'quote-last-change',
        element: (
          <>
            <Typography>Last Change</Typography>
            <Typography>{getFormattedDate(entity?.updatedOn)}</Typography>
          </>
        ),
      },
      {
        key: 'quote-premium',
        element: (
          <>
            {premium && ![QuoteStatus.Initialized, QuoteStatus.DataGather].includes(entity?.state as QuoteStatus) ? (
              <div>
                {Boolean(premium?.monthlyPremium?.amount) && (
                  <Typography className={classNames(classes.fieldValue, classes.primary)}>
                    {formatPremium(premium?.monthlyPremium?.amount, 'monthly')}
                  </Typography>
                )}
                {Boolean(premium?.annualPremium?.amount) && (
                  <Typography className={classNames(classes.fieldValue)}>
                    {formatPremium(premium?.annualPremium?.amount, 'annual')}
                  </Typography>
                )}
              </div>
            ) : null}
          </>
        ),
      },
      {
        key: 'quote-actions-btn',
        element: renderAsyncView(
          availableQuoteActionsMap,
          actions => (
            <QuoteActionMenu
              availableActions={getApplicableActions(actions[quote.entity._key.rootId]) ?? []}
              selectedQuotes={[
                {
                  productModel: entity?.productCd,
                  rootId: entity?._key?.rootId,
                  revisionNo: entity?._key?.revisionNo,
                  status: entity?.state,
                  policyNumber: entity.policyNumber,
                  effectiveDate: entity.effectiveDate,
                },
              ]}
              className={classes.primary}
              source={QuoteActionSource.QuoteList}
            />
          ),

          () => <CircularProgress size={isMobileView ? 20 : 24} />
        ),
      },
    ],
    [quote, isMobileView, isSelected, handleQuoteSelectionChange, availableQuoteActionsMap]
  );
};

type QuoteCardProps = {
  quote: OpportunityQuote;
};

const QuoteCardDesktop = (props: QuoteCardProps) => {
  const classes = useQuoteCardStyles();
  const {quote} = props;

  const fields = useQuoteCardFieldElements(quote, classes);

  return (
    <Grid container className={classes.quoteCard}>
      <Grid
        item
        className={classNames(classes.quoteField, classes.quoteSelectionCheckbox)}
        xs={1}
        sm={1}
        md={1}
        key={''}
      >
        {fields[0].element}
      </Grid>
      <Grid item className={classes.quoteField} xs={1} sm={1} md={2}>
        {fields[1].element}
      </Grid>
      <Grid item className={classes.quoteField} xs={1} sm={1} md={2}>
        {fields[3].element}
      </Grid>
      <Grid item className={classes.quoteField} xs={1} sm={1} md={1}>
        {fields[4].element}
      </Grid>
      {/* <Grid item className={classes.quoteField} xs={1} sm={1} md={1}>
        {fields[5].element}
      </Grid> */}
      <Grid item className={classes.quoteField} xs={1} sm={1} md={2}>
        {fields[6].element}
      </Grid>
      <Grid
        item
        className={classNames({
          [classes.quoteField]: true,
          [classes.quotePremium]: true,
          [classes.quotePremiumBg]: ![
            QuoteStatus.Initialized,
            QuoteStatus.DataGather,
            QuoteStatus.CompanyDeclined,
            QuoteStatus.CustomerDeclined,
          ].includes(quote.entity?.state as QuoteStatus),
        })}
        xs={1}
        sm={1}
        md={2}
      >
        {fields[7].element}
      </Grid>
      <Grid item xs={1} sm={1} md={1} className={classes.quoteAction}>
        {fields[8].element}
      </Grid>
    </Grid>
  );
};

const QuoteCardMobile = (props: QuoteCardProps) => {
  const {quote} = props;
  const classes = useQuoteCardStyles();
  const fields = useQuoteCardFieldElements(quote, classes);

  return (
    <Card className={classes.quoteCard}>
      <CardHeader
        avatar={fields[0].element}
        action={fields[8].element}
        title={fields[1].element}
        subheader={fields[2].element}
      />
      <CardContent>
        <Divider className={classes.divider} />
        <Grid container spacing={4} justifyContent="flex-start">
          <Grid item className={classes.quoteField} xs={12}>
            {fields[3].element}
          </Grid>
          <Grid item className={classes.quoteField}>
            {fields[4].element}
          </Grid>
          {/* <Grid item className={classes.quoteField}>
            {fields[5].element}
          </Grid> */}
          <Grid item className={classes.quoteField}>
            {fields[6].element}
          </Grid>
        </Grid>
      </CardContent>
      <CardActions
        className={classNames(classes.quotePremium, {
          [classes.quotePremiumBg]:
            ![QuoteStatus.Initialized, QuoteStatus.DataGather].includes(quote.entity?.state as QuoteStatus) &&
            (quote.premium?.annualPremium ?? quote.premium?.monthlyPremium),
        })}
      >
        {fields[7].element}
      </CardActions>
    </Card>
  );
};

export const LoadingQuoteList = () => {
  return (
    <>
      <Skeleton animation="pulse" width="100%" height={100} />
      <Skeleton animation="pulse" width="100%" />
      <Skeleton animation="pulse" width="100%" />
    </>
  );
};

export const QuotesList = ({filteredQuotes}: {filteredQuotes: OpportunityQuote[]}) => {
  const dispatch = useDispatch();
  const {quotes, opportunitySummary, copyQuoteAlert} = useSelector((state: ReducerState) => state.opportunity);
  const classes = useQuoteCardStyles();

  const handleAlertClose = () => {
    dispatch(clearCopyQuoteAlertMessage());
  };
  const isMobileView = !useMediaQuery((theme: any) => theme.breakpoints.up('lg'));

  return (
    <div>
      {isLoaded(opportunitySummary) ? (
        <>
          {renderAsyncView(
            quotes,
            () => (
              <>
                {copyQuoteAlert && (
                  <Alert
                    severity={copyQuoteAlert.severity}
                    className={classes[copyQuoteAlert.severity] || ''}
                    onClose={handleAlertClose}
                    sx={{
                      marginBottom: 2,
                      display: 'inline-flex',
                      maxWidth: '100%',
                      width: 'auto%',
                      backgroundColor:
                        copyQuoteAlert.severity === 'success'
                          ? themeColors.green_100
                          : copyQuoteAlert.severity === 'error'
                          ? themeColors.red_100
                          : themeColors.brightBlue_100,
                      color: themeColors.gray_800,
                    }}
                  >
                    {copyQuoteAlert.message}
                  </Alert>
                )}
                <Grid container spacing={3}>
                  {Array.isArray(filteredQuotes) && filteredQuotes.length > 0 ? (
                    filteredQuotes?.map(quote => (
                      <Grid item key={quote?.entity?._key?.rootId} style={{width: isMobileView ? 'auto' : '100%'}}>
                        {isMobileView ? <QuoteCardMobile quote={quote} /> : <QuoteCardDesktop quote={quote} />}
                      </Grid>
                    ))
                  ) : (
                    <div className={classes.noRecord}>
                      <InboxOutlined style={{color: '#D9D9D9', transform: 'scale(2)'}} />
                      <div className={classes.alignRecordCenter}>No Data</div>
                    </div>
                  )}
                </Grid>
                <ScrollToTop />
              </>
            ),
            LoadingQuoteList
          )}
        </>
      ) : null}
    </div>
  );
};
